import React, {
  PropsWithChildren,
  PureComponent,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import { usePathnameRef } from '@fhs/utils';
import { IBaseProps } from '@rbi-ctg/frontend';
import ErrorBoundary from 'components/error-boundary-static';
import logger, { LogLevel } from 'utils/logger';

interface IErrorBoundaryProps extends IBaseProps {
  onError: (e: Error, meta: object) => void;
  onRequestReload(): void;
}

export interface IErrorCtx {
  handleError: (e: Error, meta?: object) => void;
  setCurrentOrderId: (id: string) => void;
  // This ref is used to provide the current sanity data that was fetched when the app crashed for datadog logging purposes.
  setSanityDataRef: (data: any) => void;
}

type SanityData = Record<string, any> | Array<Record<string, any>> | undefined;

export const ErrorContext = createContext<IErrorCtx>({} as any as IErrorCtx);
export const useErrorContext = () => useContext(ErrorContext);

/**
 * `componentDidCatch` is only available
 * on Class components :(
 **/
const errorState: Error | null = null;
class TopLevelErrorBoundary extends PureComponent<IErrorBoundaryProps> {
  state = {
    hasError: false,
    error: errorState,
  };

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    const { onError } = this.props;
    onError(error, { info, level: LogLevel.fatal });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundary
          onRequestReload={this.props.onRequestReload}
          errorReason={this.state.error?.toString() ?? 'Unknown reason'}
        />
      );
    }
    return this.props.children;
  }
}

export function ErrorsProvider({
  children,
  onRequestReload,
}: PropsWithChildren<{
  onRequestReload: () => void;
}>) {
  const { getCurrentPathname } = usePathnameRef();

  const orderId = useRef<string>('');
  const sanityDataRef = useRef<SanityData>();
  const setCurrentOrderId = useCallback((serverOrderId: string) => {
    orderId.current = serverOrderId;
  }, []);

  const setSanityDataRef = useCallback((data: any) => {
    sanityDataRef.current = data;
  }, []);

  const sanityData = sanityDataRef.current;

  const handleError = useCallback(
    (error: Error, meta: object = {}) => {
      error.message = `Error Boundary: ${error.message}`;
      const errorMeta = {
        ...meta,
        'Transaction RBI Cloud Order ID': orderId.current,
        'Source Page': getCurrentPathname(),
        errorBoundary: true,
        containsSanityData: !!sanityData,
        // Only apply the sanity data key if sanity data is available
        ...(sanityData && { sanityData }),
      };

      logger.error({ error, context: { ...errorMeta } });
      // If sanity data was present, clear the data after logging.
      if (sanityDataRef.current) {
        setSanityDataRef(undefined);
      }
    },
    [getCurrentPathname, setSanityDataRef, sanityData]
  );

  const providerValue = useMemo(
    () => ({ handleError, setCurrentOrderId, setSanityDataRef }),
    [handleError, setCurrentOrderId, setSanityDataRef]
  );

  return (
    <ErrorContext.Provider value={providerValue}>
      <TopLevelErrorBoundary onError={handleError} onRequestReload={onRequestReload}>
        {children}
      </TopLevelErrorBoundary>
    </ErrorContext.Provider>
  );
}
